import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FaFilePdf,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaDownload,
  FaFileWord,
  FaArrowLeft,
  FaEdit,
  FaChevronDown,
  FaCheckCircle,
} from "react-icons/fa";
import { FiSearch } from "react-icons/fi";

import { getData, postData } from "../../services/FetchNodeservices";

const getCandidateKey = (c) => c.resume_id || c.candidate_id || c.id;

function SearchableJobDropdown({ jobs, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedJob = jobs.find((j) => String(j.reqId) === String(value));

  const filteredJobs = useMemo(() => {
    if (!search.trim()) return jobs;
    const term = search.toLowerCase();
    return jobs.filter(
      (j) =>
        String(j.reqId || "").toLowerCase().includes(term) ||
        String(j.title || "").toLowerCase().includes(term) ||
        String(j.company_name || j.company || "").toLowerCase().includes(term) ||
        String(j.location || "").toLowerCase().includes(term)
    );
  }, [jobs, search]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full h-9 px-3 bg-white border border-gray-200 rounded-xl text-[11px] font-semibold text-gray-800 focus:outline-none focus:border-indigo-500 transition duration-200 cursor-pointer flex items-center justify-between gap-1 shadow-sm hover:bg-gray-50/80"
      >
        <span className="truncate text-left font-medium">
          {selectedJob
            ? `${selectedJob.reqId} | ${selectedJob.title} | ${selectedJob.location || "Location NA"}`
            : "Select Job..."}
        </span>
        <FaChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 p-2.5 max-h-72 flex flex-col gap-1.5 animate-fadeIn w-[320px] sm:w-[420px] max-w-[90vw]">
          {/* SEARCH INPUT */}
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search req ID, job title, company..."
              className="w-full h-8 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-[11px] font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition"
              autoFocus
            />
            <FiSearch className="absolute left-2.5 top-2.5 text-gray-400 w-3 h-3" />
          </div>

          {/* JOBS LIST */}
          <div className="overflow-y-auto max-h-52 space-y-1 divide-y divide-gray-50 pr-0.5">
            {filteredJobs.length === 0 ? (
              <div className="p-3 text-center text-xs text-gray-400 font-semibold">
                No matching jobs found
              </div>
            ) : (
              filteredJobs.map((job) => {
                const isSelected = String(job.reqId) === String(value);
                return (
                  <div
                    key={job.reqId}
                    onClick={() => {
                      onChange(job.reqId);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className={`px-2.5 py-2 rounded-lg text-xs font-medium cursor-pointer transition flex flex-col gap-1 ${
                      isSelected
                        ? "bg-indigo-50 text-indigo-700 font-bold"
                        : "hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-extrabold text-indigo-950 text-xs leading-snug break-words whitespace-normal">
                        {job.reqId} — {job.title}
                      </span>
                      {job.no_of_openings && (
                        <span className="text-[10px] font-bold px-1.5 py-0.2 bg-gray-100 text-gray-600 rounded flex-shrink-0">
                          {job.no_of_openings} open
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 font-semibold leading-normal break-words whitespace-normal">
                      {job.company_name || job.company || "Company NA"} • {job.location || "Location NA"}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TalentPoolResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [previewResume, setPreviewResume] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState({});
  const [assignedJobsState, setAssignedJobsState] = useState({});
  const [commentsState, setCommentsState] = useState({});
  const [savingComment, setSavingComment] = useState({});
  const [ctcDetailsState, setCtcDetailsState] = useState({});
  const [savingDetails, setSavingDetails] = useState({});

  const page = Number(searchParams.get("page")) || 1;
  const limit = 10;

  useEffect(() => {
    fetchTalentPoolData();
    // eslint-disable-next-line
  }, [searchParams]);

  const fetchTalentPoolData = async () => {
    const params = new URLSearchParams(searchParams);
    if (!params.has("page")) params.set("page", page.toString());
    params.set("limit", limit.toString());

    let result = await getData(`api/talentpool?${params.toString()}`);
    if (!result || typeof result !== "object" || result.status === undefined) {
      result = await getData(`talentpool?${params.toString()}`);
    }

    if (result?.status) {
      setData(result.data || []);
      setTotal(result.total || 0);
    } else {
      setData([]);
      setTotal(0);
    }
  };

  const handlePageChange = (newPageNo) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPageNo.toString());
    navigate(`/dashboard/talentpool/results?${newParams.toString()}`);
  };

  const handleModifySearch = () => {
    const params = new URLSearchParams(searchParams);
    navigate(`/dashboard/talentpool?${params.toString()}`);
  };

  const getSecureUrl = (url) => {
    if (!url) return "";
    if (window.location.protocol === "https:" && url.startsWith("http:")) {
      return url.replace("http:", "https:");
    }
    return url;
  };

  const handleViewResume = (c) => {
    const isPdf = c.filename ? c.filename.toLowerCase().endsWith(".pdf") : true;
    setPreviewResume({
      url: getSecureUrl(c.resume),
      filename: c.filename,
      candidateName: c.full_name,
      isPdf,
    });
  };

  const handleDownload = async (e, url, filename) => {
    e.preventDefault();
    const secureUrl = getSecureUrl(url);
    try {
      const response = await fetch(`${secureUrl}?download=true`);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "resume.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 2000);
    } catch (error) {
      console.error("Javascript download failed, falling back to browser download:", error);
      window.open(`${secureUrl}?download=true`, "_blank");
    }
  };

  const fetchJobsList = async () => {
    try {
      const res = await getData("job/jobs");
      const list = res?.jobs || res?.data || [];
      setJobs(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("fetchJobsList error:", err);
    }
  };

  useEffect(() => {
    fetchJobsList();
  }, []);

  const handleAssignToJob = async (resumeId, jobId) => {
    if (!resumeId || !jobId) return;

    // Find the candidate object using the passed key (candKey)
    const candidate = data.find((item) => getCandidateKey(item) === resumeId);
    if (!candidate) {
      alert("Candidate details not found.");
      return;
    }

    const userData = JSON.parse(localStorage.getItem("USER")) || {};
    const addedById = userData.id;
    const addedByName = `${userData.first_name || ""} ${userData.last_name || ""}`.trim();

    try {
      let response = await postData("api/talentpool/assign-to-job", {
        source: candidate.source || "resume",
        resumeId: candidate.resume_id,
        candidateId: candidate.candidate_id,
        jobId,
        addedById,
        addedByName
      });
      if (!response || response.status === undefined) {
        response = await postData("talentpool/assign-to-job", {
          source: candidate.source || "resume",
          resumeId: candidate.resume_id,
          candidateId: candidate.candidate_id,
          jobId,
          addedById,
          addedByName
        });
      }

      if (response?.status) {
        alert(response.message || "Candidate successfully assigned to job!");
        const matchedJob = jobs.find((j) => String(j.reqId) === String(jobId));
        const assignedItem = { req_id: jobId, title: matchedJob?.title || jobId };
        setAssignedJobsState((prev) => ({
          ...prev,
          [resumeId]: [...(prev[resumeId] || []), assignedItem],
        }));
        setSelectedJobs(prev => {
          const updated = { ...prev };
          delete updated[resumeId];
          return updated;
        });
      } else {
        alert(response?.message || "Failed to assign candidate to job.");
      }
    } catch (error) {
      console.error("Assignment error:", error);
      alert("An error occurred while assigning the candidate.");
    }
  };

  const handleSaveComment = async (candKey, source, id, commentText) => {
    if (!id) return;

    setSavingComment((prev) => ({ ...prev, [candKey]: true }));
    try {
      let response = await postData("api/talentpool/comment", {
        source,
        id,
        comment: commentText
      });
      if (!response || response.status === undefined) {
        response = await postData("talentpool/comment", {
          source,
          id,
          comment: commentText
        });
      }

      if (response?.status) {
        alert("Comment saved successfully!");
        setData((prevData) =>
          prevData.map((cand) =>
            getCandidateKey(cand) === candKey
              ? { ...cand, comments: commentText }
              : cand
          )
        );
      } else {
        alert(response?.message || "Failed to save comment.");
      }
    } catch (error) {
      console.error("Error saving comment:", error);
      alert("An error occurred while saving the comment.");
    } finally {
      setSavingComment((prev) => ({ ...prev, [candKey]: false }));
    }
  };

  const handleSaveDetails = async (candKey, source, id, details) => {
    if (!id) return;

    setSavingDetails((prev) => ({ ...prev, [candKey]: true }));
    try {
      let response = await postData("api/talentpool/update-details", {
        source,
        id,
        current_ctc: details.current_ctc,
        expected_ctc: details.expected_ctc,
        notice_period: details.notice_period
      });
      if (!response || response.status === undefined) {
        response = await postData("talentpool/update-details", {
          source,
          id,
          current_ctc: details.current_ctc,
          expected_ctc: details.expected_ctc,
          notice_period: details.notice_period
        });
      }

      if (response?.status) {
        alert("Candidate details saved successfully!");
        setData((prevData) =>
          prevData.map((cand) =>
            getCandidateKey(cand) === candKey
              ? { 
                  ...cand, 
                  current_ctc: details.current_ctc, 
                  expected_ctc: details.expected_ctc, 
                  notice_period: details.notice_period 
                }
              : cand
          )
        );
      } else {
        alert(response?.message || "Failed to save candidate details.");
      }
    } catch (error) {
      console.error("Error saving candidate details:", error);
      alert("An error occurred while saving candidate details.");
    } finally {
      setSavingDetails((prev) => ({ ...prev, [candKey]: false }));
    }
  };

  const totalPages = Math.ceil(total / limit);

  // Compile active search parameters for the query chip bar
  const queryKeys = ["q", "name", "expMin", "expMax", "qualification", "designation", "company", "city", "state", "country", "notice_period", "ctcMin", "ctcMax"];
  const activeFilters = [];
  queryKeys.forEach(key => {
    const val = searchParams.get(key);
    if (val && val.trim() !== "") {
      activeFilters.push({ key, val: val.trim() });
    }
  });

  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans p-2">

      {/* HEADER & TOP CONTROLS */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            <button
              onClick={() => navigate("/dashboard/talentpool")}
              className="p-2 hover:bg-gray-100 rounded-xl transition duration-150 text-gray-400 hover:text-gray-700"
              title="Back to search form"
            >
              <FaArrowLeft className="w-4 h-4" />
            </button>
            Search Results
          </h1>
          <p className="text-sm font-semibold text-gray-400 mt-1">
            Displaying matching applicant profiles from the database
          </p>
        </div>

        <button
          onClick={handleModifySearch}
          className="flex items-center justify-center gap-2 bg-indigo-650 hover:bg-indigo-750 text-white bg-indigo-600 rounded-2xl px-5 py-2.5 text-xs font-bold shadow-lg shadow-indigo-100 transition duration-200"
        >
          <FaEdit /> Modify Search
        </button>
      </div>

      {/* ACTIVE QUERY HEADER */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-100 rounded-2xl p-4 shadow-lg shadow-indigo-100/5">
          <span className="text-xs font-black text-indigo-950 uppercase tracking-wider mr-2">
            Active Query:
          </span>
          {activeFilters.map(({ key, val }) => (
            <span
              key={key}
              className="px-3 py-1.5 rounded-xl bg-indigo-50/50 border border-indigo-100 text-indigo-700 text-xs font-semibold flex items-center gap-1.5"
            >
              <span className="font-bold uppercase text-[10px] text-indigo-400">
                {key === "q" ? "Keywords" : key === "expMin" ? "Min Exp" : key === "expMax" ? "Max Exp" : key === "ctcMin" ? "Min CTC" : key === "ctcMax" ? "Max CTC" : key}:
              </span>
              {val}
            </span>
          ))}
        </div>
      )}

      {/* RESULTS LIST - FULL WIDTH */}
      <div className="w-full space-y-6">
        {/* Stats */}
        <div className="text-gray-500 text-xs font-semibold pl-1">
          Showing{" "}
          <span className="text-gray-900 font-black">{data.length}</span>{" "}
          results of{" "}
          <span className="text-gray-900 font-black">{total}</span>
        </div>

        {/* Candidate Cards Grid/List */}
        <div className="space-y-4">
          {data.map((c) => {
            const candKey = getCandidateKey(c);
            const rawAssigned = [
              ...(c.assigned_jobs || []),
              ...(assignedJobsState[candKey] || []),
            ];
            const assignedList = rawAssigned.filter(
              (v, idx, arr) => arr.findIndex((t) => t.req_id === v.req_id) === idx
            );

            return (
              <div
                key={candKey}
                className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-indigo-100/10 hover:border-indigo-200 transition duration-200 w-full"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="space-y-2.5 flex-1">
                    <div>
                      <h3 className="text-base font-black text-indigo-950">
                        {c.full_name}
                      </h3>
                      <p className="text-xs font-bold text-gray-400 mt-0.5">
                        {c.designation || "—"} • {c.current_company || "—"}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                      <Tag label={`${c.experience || 0} yrs`} />
                      <Tag label={c.location || "Location NA"} />
                      {c.current_ctc && (
                        <Tag label={`Current CTC: ${c.current_ctc} LPA`} />
                      )}
                      {c.expected_ctc && (
                        <Tag label={`Expected CTC: ${c.expected_ctc} LPA`} />
                      )}
                      {c.notice_period && (
                        <Tag label={`NP: ${c.notice_period}`} />
                      )}
                      {assignedList.map((aj) => (
                        <span
                          key={aj.req_id}
                          className="px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-extrabold tracking-wider flex items-center gap-1 shadow-xs"
                        >
                          <FaCheckCircle className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                          Assigned to Job: {aj.req_id} {aj.title ? `(${aj.title})` : ""}
                        </span>
                      ))}
                    </div>

                    <div className="text-xs text-gray-600 space-y-1 pt-2">
                      <p className="flex items-center gap-1.5"><span className="text-gray-400 font-semibold">Email:</span> {c.email_id || "-"}</p>
                      <p className="flex items-center gap-1.5"><span className="text-gray-400 font-semibold">Phone:</span> {c.phone_number || "-"}</p>
                      <p className="flex items-center gap-1.5"><span className="text-gray-400 font-semibold">Degree:</span> {c.qualification || "-"}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 min-w-[220px] w-full md:w-auto self-stretch md:self-auto justify-end border border-gray-100 rounded-2xl p-4 bg-gray-50/20">
                    <button
                      onClick={() => handleViewResume(c)}
                      className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm rounded-2xl px-4 py-2.5 text-xs font-bold transition duration-200 w-full"
                    >
                      {c.filename?.toLowerCase().endsWith(".pdf") ? (
                        <FaFilePdf className="text-red-500 w-3.5 h-3.5" />
                      ) : (
                        <FaFileWord className="text-blue-500 w-3.5 h-3.5" />
                      )}
                      View Resume
                    </button>

                    <a
                      href={`${getSecureUrl(c.resume)}?download=true`}
                      download={c.filename}
                      onClick={(e) => handleDownload(e, c.resume, c.filename)}
                      className="flex items-center justify-center gap-2 bg-indigo-650 hover:bg-indigo-750 text-white bg-indigo-600 rounded-2xl px-4 py-2.5 text-xs font-bold shadow-lg shadow-indigo-100 transition duration-200 w-full"
                    >
                      <FaDownload className="w-3.5 h-3.5" /> Download
                    </a>

                    {/* Job Assignment */}
                    <div className="border-t border-gray-200/60 pt-2.5 mt-1 space-y-2">
                      <SearchableJobDropdown
                        jobs={jobs}
                        value={selectedJobs[candKey] || ""}
                        onChange={(val) =>
                          setSelectedJobs({ ...selectedJobs, [candKey]: val })
                        }
                      />
                      <button
                        onClick={() => handleAssignToJob(candKey, selectedJobs[candKey])}
                        disabled={!selectedJobs[candKey]}
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-[11px] font-bold rounded-xl shadow-sm transition duration-200"
                      >
                        Assign to Job
                      </button>
                    </div>
                  </div>
                </div>

                {/* CTC and Notice Period Section */}
                <div className="mt-4 pt-4 border-t border-gray-100/80 flex flex-col gap-2.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-indigo-950/70 pl-1">
                    Candidate CTC & Notice Period Details
                  </label>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                    {/* Current CTC */}
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-gray-500 pl-1">Current CTC (LPA)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={ctcDetailsState[candKey]?.current_ctc ?? (c.current_ctc || "")}
                        onChange={(e) => {
                          const currentVal = ctcDetailsState[candKey] || {
                            current_ctc: c.current_ctc || "",
                            expected_ctc: c.expected_ctc || "",
                            notice_period: c.notice_period || ""
                          };
                          setCtcDetailsState({
                            ...ctcDetailsState,
                            [candKey]: { ...currentVal, current_ctc: e.target.value }
                          });
                        }}
                        placeholder="e.g. 8.5"
                        className="h-9 px-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition"
                      />
                    </div>

                    {/* Expected CTC */}
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-gray-500 pl-1">Expected CTC (LPA)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={ctcDetailsState[candKey]?.expected_ctc ?? (c.expected_ctc || "")}
                        onChange={(e) => {
                          const currentVal = ctcDetailsState[candKey] || {
                            current_ctc: c.current_ctc || "",
                            expected_ctc: c.expected_ctc || "",
                            notice_period: c.notice_period || ""
                          };
                          setCtcDetailsState({
                            ...ctcDetailsState,
                            [candKey]: { ...currentVal, expected_ctc: e.target.value }
                          });
                        }}
                        placeholder="e.g. 12.0"
                        className="h-9 px-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition"
                      />
                    </div>

                    {/* Notice Period */}
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-gray-500 pl-1">Notice Period</span>
                      <select
                        value={ctcDetailsState[candKey]?.notice_period ?? (c.notice_period || "")}
                        onChange={(e) => {
                          const currentVal = ctcDetailsState[candKey] || {
                            current_ctc: c.current_ctc || "",
                            expected_ctc: c.expected_ctc || "",
                            notice_period: c.notice_period || ""
                          };
                          setCtcDetailsState({
                            ...ctcDetailsState,
                            [candKey]: { ...currentVal, notice_period: e.target.value }
                          });
                        }}
                        className="h-9 px-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-750 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition"
                      >
                        <option value="">Select notice period...</option>
                        <option value="Immediate">Immediate</option>
                        <option value="15 Days">15 Days</option>
                        <option value="30 Days">30 Days (1 Month)</option>
                        <option value="45 Days">45 Days</option>
                        <option value="60 Days">60 Days (2 Months)</option>
                        <option value="90 Days">90 Days (3 Months)</option>
                        <option value="More than 3 Months">More than 3 Months</option>
                      </select>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={() => {
                        const details = ctcDetailsState[candKey] || {
                          current_ctc: c.current_ctc || "",
                          expected_ctc: c.expected_ctc || "",
                          notice_period: c.notice_period || ""
                        };
                        const dbId = c.source === "resume" ? c.resume_id : c.candidate_id;
                        handleSaveDetails(candKey, c.source, dbId, details);
                      }}
                      disabled={savingDetails[candKey]}
                      className="h-9 bg-indigo-600 hover:bg-indigo-755 text-white bg-indigo-600 rounded-xl text-xs font-bold shadow-sm transition duration-150 flex items-center justify-center cursor-pointer disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {savingDetails[candKey] ? "Saving..." : "Save Details"}
                    </button>
                  </div>
                </div>

                {/* Recruiter Comments Section */}
                <div className="mt-4 pt-4 border-t border-gray-100/80 flex flex-col gap-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-indigo-950/70 pl-1">
                    Recruiter Comments / Notes
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      rows={1}
                      value={commentsState[candKey] ?? (c.comments || "")}
                      onChange={(e) => setCommentsState({ ...commentsState, [candKey]: e.target.value })}
                      placeholder="Add recruiter comments/notes for this candidate..."
                      className="flex-1 min-h-[36px] max-h-[120px] px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition"
                    />
                    <button
                      onClick={() => {
                        const dbId = c.source === "resume" ? c.resume_id : c.candidate_id;
                        handleSaveComment(candKey, c.source, dbId, commentsState[candKey] ?? (c.comments || ""));
                      }}
                      disabled={savingComment[candKey]}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-750 text-white rounded-xl text-xs font-bold shadow-sm transition duration-150 flex items-center justify-center min-w-[70px] cursor-pointer disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {savingComment[candKey] ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {data.length === 0 && (
            <div className="bg-white border border-gray-100 rounded-3xl p-16 text-center text-gray-400 font-semibold shadow-md">
              No candidate resumes found. Try adjusting search parameters.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between bg-white border border-gray-100 rounded-3xl p-4 shadow-xl shadow-indigo-100/10">
            <button
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
              className="px-4 py-2.5 rounded-2xl bg-white border border-gray-250 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition duration-200 border-gray-200"
            >
              <FaChevronLeft className="w-3 h-3" />
            </button>

            <div className="text-xs font-bold text-gray-400">
              Page <span className="text-gray-900 font-black">{page}</span> of <span className="text-gray-900 font-black">{totalPages}</span>
            </div>

            <button
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
              className="px-4 py-2.5 rounded-2xl bg-white border border-gray-250 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition duration-200 border-gray-200"
            >
              <FaChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* RESUME PREVIEW MODAL */}
      {previewResume && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl border border-gray-100 flex flex-col h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-base font-black text-gray-900 truncate">
                  {previewResume.candidateName}'s Resume
                </h3>
                <p className="text-xs text-gray-400 font-semibold truncate mt-0.5">
                  {previewResume.filename}
                </p>
              </div>
              <button
                onClick={() => setPreviewResume(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition duration-150"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 flex flex-col justify-center items-center">
              {previewResume.isPdf ? (
                <iframe
                  src={previewResume.url}
                  className="w-full h-full rounded-2xl border border-gray-250/85 shadow-inner bg-white"
                  title={`Resume Preview - ${previewResume.candidateName}`}
                />
              ) : (
                <div className="flex flex-col items-center justify-center p-12 bg-white rounded-3xl border border-gray-100 shadow-xl max-w-md text-center">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-4 border border-blue-100">
                    <FaFileWord className="w-8 h-8" />
                  </div>
                  <h4 className="text-base font-black text-gray-800">
                    Word Document Preview Unavailable
                  </h4>
                  <p className="text-xs text-gray-400 font-semibold mt-2 max-w-xs leading-relaxed">
                    Browsers cannot render Word documents (.doc/.docx) inline natively. You can download and read the resume offline.
                  </p>
                  <a
                    href={`${previewResume.url}?download=true`}
                    download={previewResume.filename}
                    onClick={(e) => handleDownload(e, previewResume.url, previewResume.filename)}
                    className="mt-6 flex items-center justify-center gap-2 bg-indigo-650 hover:bg-indigo-750 text-white bg-indigo-600 rounded-2xl px-6 py-3 text-xs font-bold shadow-lg transition duration-200"
                  >
                    <FaDownload className="w-3.5 h-3.5" /> Download Word File
                  </a>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-end gap-3">
              <button
                onClick={() => setPreviewResume(null)}
                className="px-5 py-2.5 bg-white hover:bg-gray-50 border border-gray-250 text-gray-700 rounded-2xl text-xs font-bold transition duration-200"
              >
                Close
              </button>
              <a
                href={`${previewResume.url}?download=true`}
                download={previewResume.filename}
                onClick={(e) => handleDownload(e, previewResume.url, previewResume.filename)}
                className="flex items-center gap-2 bg-indigo-650 hover:bg-indigo-750 text-white bg-indigo-600 rounded-2xl px-5 py-2.5 text-xs font-bold shadow-lg shadow-indigo-100 transition duration-200"
              >
                <FaDownload className="w-3.5 h-3.5" /> Download Resume
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tag({ label }) {
  return (
    <span className="px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100/55 text-indigo-600 text-[10px] font-extrabold tracking-wider">
      {label}
    </span>
  );
}
